import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import {
	Autocomplete,
	TextField,
	CircularProgress,
	useMediaQuery,
	styled,
	Typography,
	Box,
	Skeleton,
	Button,
	Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import { useOptions } from "../../context/optionContext";
import { useDebounce } from "../../hooks/useDebounce";
import { updateRecentSites as updateRecentSitesService } from "../../services/userService";
import { buildSiteSearchIndex, searchSiteIndex } from "../../utilities/siteSearch";
import { DEFAULT_LAYOUT } from "../../config";

const RECENT_SITES_KEY = "iecom_recent_sites";
const MAX_RECENT_SITES = 5;
const DEFAULT_OPTION_COUNT = 5;
const SEARCH_DEBOUNCE_MS = 250;
const ADMIN_HINT_VALUE = "__admin_all_sites_hint__";
const GLOBAL_OPTION_VALUE = "__global_site__";
const IS_LAYOUT2 = String(DEFAULT_LAYOUT || "").toLowerCase() === "layout2";
const FIELD_WIDTH = IS_LAYOUT2 ? 225 : 165;

const GroupHeader = styled("div")({
	position: "sticky",
	top: 0,
	padding: "4px 8px",
	color: "#181818",
	backgroundColor: "#eee",
	fontWeight: 600,
	fontSize: "12.5px",
});

const GroupItems = styled("ul")({
	padding: 0,
	margin: 0,
	listStyle: "none",
});

/**
 * Reads a feature flag that may arrive as a boolean or as the strings
 * "false" / "0" from the options EAV table.
 *
 * @param {*} value Raw flag value.
 * @param {boolean} [defaultValue=true] Used when the flag is absent.
 * @returns {boolean} Whether the feature is on.
 */
const isEnabled = (value, defaultValue = true) => {
	if (value === undefined || value === null) return defaultValue;
	if (typeof value === "boolean") return value;
	return value !== "false" && value !== "0";
};

/**
 * Reads the recent-site list from localStorage, falling back to the copy the
 * server put on the user record at login.
 *
 * @param {string[]|undefined} userRecentSites `user.recent_sites` from the auth context.
 * @returns {string[]} Site values, most recent first; empty when nothing is stored.
 */
const readStoredRecentSites = (userRecentSites) => {
	try {
		const parsed = JSON.parse(localStorage.getItem(RECENT_SITES_KEY) || "[]");
		if (Array.isArray(parsed) && parsed.length > 0) return parsed;
	} catch (_) {
		// storage unavailable or corrupt; fall through to the server copy
	}
	return Array.isArray(userRecentSites) ? userRecentSites : [];
};

/**
 * Group key used by the Autocomplete: the parent bakery (customer) name.
 *
 * @param {object} option A site option.
 * @returns {string} Group name, "" for options without a parent (hint rows).
 */
const getGroupName = (option) => option?.parent_customer_name || "";

/**
 * Reorders options so every group is contiguous. Groups keep the order of
 * their first appearance and options keep their order inside a group.
 *
 * MUI Autocomplete renders a duplicate header every time a group re-appears
 * in the list, so relevance-ordered search results must be regrouped before
 * they reach `groupBy`.
 *
 * @param {object[]} options Options in relevance (or alphabetical) order.
 * @param {(option: object) => string} getGroup Group key selector.
 * @returns {object[]} The same options, grouped.
 */
const groupContiguously = (options, getGroup) => {
	const firstSeen = new Map();
	options.forEach((option, index) => {
		const group = getGroup(option);
		if (!firstSeen.has(group)) firstSeen.set(group, index);
	});
	return options
		.map((option, index) => ({ option, index }))
		.sort(
			(a, b) =>
				firstSeen.get(getGroup(a.option)) - firstSeen.get(getGroup(b.option)) || a.index - b.index
		)
		.map(({ option }) => option);
};

/**
 * Header "Select Bakery Location" picker: a grouped MUI Autocomplete over every
 * site the user may see, with a client-side fuzzy search and a recent-sites
 * shortlist.
 *
 * Search is entirely client-side (`siteContext` loads the full list once), so
 * the only things that can break it are the component's own input handling.
 * Two rules keep it working: the typed text is cleared only when the popup
 * opens by focus/click (never on a click inside the popup, which React bubbles
 * up through the portal, and never on the keystroke that re-opens it), and the
 * selected site is never pushed back into the auth user object, because that
 * used to re-fetch every site on each selection.
 *
 * @returns {JSX.Element} The site selector, or a skeleton while sites load.
 */
const SiteSelection = () => {
	const { selectedSite, updateSite, siteOptions, loading, siteData, isAdminAllSites } = useSite();
	const { user } = useAuth();
	const { optionKeys } = useOptions();
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const isMobile = useMediaQuery("(max-width: 768px)");
	const isAdminRoute = (pathname || "").toLowerCase().startsWith("/admin");

	const inputRef = useRef(null);
	const [inputValue, setInputValue] = useState("");
	const [recentSiteValues, setRecentSiteValues] = useState([]);
	const searchTerm = useDebounce(inputValue.trim(), SEARCH_DEBOUNCE_MS);
	const isSearching = !isAdminRoute && inputValue.trim() !== searchTerm;

	const groupingEnabled = isEnabled(optionKeys?.siteSelectionGroupingEnabled, true);
	const showSiteNumber = isEnabled(optionKeys?.siteSelectionShowSiteNumber, true);

	const searchIndex = useMemo(() => buildSiteSearchIndex(siteOptions), [siteOptions]);

	const selectedOption = useMemo(
		() =>
			siteOptions.find((option) => option.value === selectedSite || option.text === selectedSite) ||
			null,
		[siteOptions, selectedSite]
	);

	const globalOption = useMemo(
		() => ({ value: GLOBAL_OPTION_VALUE, text: t("LABELS.GLOBAL") || "Global", disabled: true }),
		[t]
	);

	const adminHintOption = useMemo(
		() => ({
			value: ADMIN_HINT_VALUE,
			text: `${t("UI_TEXT.ADMIN_ALL_SITES_ACCESS", { defaultValue: "Admin access: all sites available." })} ${t("UI_TEXT.ADMIN_ALL_SITES_HINT", { defaultValue: "Showing recent sites by default. Search to see more." })}`,
			isHint: true,
			disabled: true,
		}),
		[t]
	);

	const userRecentSites = user?.recent_sites;
	useEffect(() => {
		const valid = new Set(siteOptions.map((option) => option.value));
		const pruned = readStoredRecentSites(userRecentSites)
			.filter((value) => valid.has(value))
			.slice(0, MAX_RECENT_SITES);
		if (pruned.length > 0) setRecentSiteValues(pruned);
	}, [siteOptions, userRecentSites]);

	const options = useMemo(() => {
		if (isAdminRoute) return [globalOption];

		let matches;
		if (searchTerm) {
			matches = searchSiteIndex(searchIndex, searchTerm);
		} else {
			const recentSet = new Set(recentSiteValues);
			const recent = recentSiteValues
				.map((value) => siteOptions.find((option) => option.value === value))
				.filter(Boolean);
			const fill = siteOptions
				.filter((option) => !recentSet.has(option.value))
				.slice(0, Math.max(0, DEFAULT_OPTION_COUNT - recent.length));
			matches = [...recent, ...fill].sort((a, b) => (a.text || "").localeCompare(b.text || ""));
		}

		// The current value must be present in `options` or MUI logs a warning; pin it first.
		if (selectedOption) {
			matches = [selectedOption, ...matches.filter((option) => option.value !== selectedOption.value)];
			if (!searchTerm) matches = matches.slice(0, DEFAULT_OPTION_COUNT);
		}
		if (groupingEnabled) matches = groupContiguously(matches, getGroupName);
		return isAdminAllSites && !searchTerm ? [adminHintOption, ...matches] : matches;
	}, [
		adminHintOption,
		globalOption,
		groupingEnabled,
		isAdminAllSites,
		isAdminRoute,
		recentSiteValues,
		searchIndex,
		searchTerm,
		selectedOption,
		siteOptions,
	]);

	/**
	 * Stores the recent-site list locally and on the server.
	 *
	 * Deliberately does not write it into the auth user object: a changed user
	 * object makes `siteContext` re-fetch every site, which happened on every
	 * selection and also reset the Autocomplete input mid-typing.
	 *
	 * @param {string[]} nextValues Site values, most recent first.
	 * @returns {void}
	 */
	const persistRecentSites = useCallback((nextValues) => {
		const limited = nextValues.slice(0, MAX_RECENT_SITES);
		setRecentSiteValues(limited);
		try {
			localStorage.setItem(RECENT_SITES_KEY, JSON.stringify(limited));
		} catch (_) {
			// storage unavailable; the server copy still gets written
		}
		// handleRequest swallows HTTP errors; the catch only guards a network-level throw.
		Promise.resolve(updateRecentSitesService(limited)).catch((err) =>
			console.error("Failed to persist recent sites", err)
		);
	}, []);

	if (loading) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, width: 250 }}>
				<Skeleton variant="circular" width={30} height={30} />
				<Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
					<Skeleton variant="text" width="80%" height={28} />
					<Skeleton variant="text" width="60%" height={20} />
				</Box>
			</Box>
		);
	}

	const value = isAdminRoute ? globalOption : selectedOption;
	const selectedSiteTooltip = value?.text || "";
	const helperTextKey = isAdminRoute || IS_LAYOUT2 ? null : "PLACEHOLDER.CHANGE_CUSTOMER";
	const inputPlaceholder = isAdminRoute ? t("PLACEHOLDER.GLOBAL_SITE_OPTION") : t("PLACEHOLDER.SELECT_SITE");
	const adminTooltip =
		t("UI_TEXT.ADMIN_SITE_LOCK") || t("EULA_ADMIN.SITE_SELECTION_IS_LOCKED_WHILE_ON_ADMIN_PAGE");

	/**
	 * Clears the search when the popup opens by focus or click so the default
	 * shortlist shows. When the popup opens because the user started typing,
	 * MUI passes the input `change` event; that keystroke must be kept,
	 * otherwise the first letter of every search is lost.
	 *
	 * @param {React.SyntheticEvent} event The event MUI opened the popup for.
	 * @returns {void}
	 */
	const handleOpen = (event) => {
		if (event?.type !== "change") setInputValue("");
	};

	/**
	 * Applies the chosen site and records it as recent. Disabled rows (the
	 * admin hint, the global placeholder) are ignored.
	 *
	 * The input is blurred afterwards on purpose: with `openOnFocus`, a text box
	 * that keeps focus after a selection re-opens the popup (and clears the
	 * search) every time the browser tab regains focus, because the browser
	 * restores focus to it and MUI treats that as a fresh focus.
	 *
	 * @param {React.SyntheticEvent} event Unused; MUI signature.
	 * @param {object|null} newValue The chosen option.
	 * @returns {void}
	 */
	const handleChange = (event, newValue) => {
		if (isAdminRoute || !newValue || newValue.disabled) return;
		updateSite(newValue.value, siteData);
		persistRecentSites([newValue.value, ...recentSiteValues.filter((v) => v !== newValue.value)]);
		inputRef.current?.blur();
	};

	/**
	 * Focuses the input when the icon or helper text is clicked. It must not
	 * touch the search text, and it must ignore clicks that originate inside
	 * the popup: those bubble here through the portal, and re-focusing the
	 * input after an option click would undo the blur in `handleChange`,
	 * leaving the box focused so the popup re-opens whenever the browser tab
	 * regains focus.
	 *
	 * @param {React.MouseEvent} event The click event.
	 * @returns {void}
	 */
	const handleContainerClick = (event) => {
		if (isAdminRoute) return;
		if (event?.target?.closest?.(".header-autocomplete-popper")) return;
		inputRef.current?.focus();
	};

	/**
	 * Renders the text field with the selected-site tooltip and search spinner.
	 *
	 * @param {object} params Render params from MUI Autocomplete.
	 * @returns {JSX.Element} The input element.
	 */
	const renderInput = (params) => (
		<Tooltip title={selectedSiteTooltip} arrow disableHoverListener={!selectedSiteTooltip}>
			<TextField
				{...params}
				placeholder={inputPlaceholder}
				variant="standard"
				inputRef={inputRef}
				inputProps={{ ...params.inputProps, "aria-label": inputPlaceholder }}
				InputProps={{
					...params.InputProps,
					disableUnderline: true,
					endAdornment: (
						<>
							{isSearching ? <CircularProgress color="primary" size={16} /> : null}
							{params.InputProps.endAdornment}
						</>
					),
				}}
				sx={{
					"& .MuiInputBase-root": {
						border: "none",
						fontWeight: "bold",
						color: "#4f4f4f",
						padding: 0,
						width: isMobile ? "100%" : FIELD_WIDTH,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					},
				}}
			/>
		</Tooltip>
	);

	/**
	 * Renders one listbox row. Both variants keep MUI's option props on the
	 * `li` so keyboard navigation and ARIA roles stay intact; the hint row is
	 * disabled through `getOptionDisabled` and only overrides the dimmed look.
	 *
	 * @param {object} props Option props from MUI (includes `key`).
	 * @param {object} option The option being rendered.
	 * @returns {JSX.Element} The list item.
	 */
	const renderOption = (props, option) => {
		const { key, ...optionProps } = props;
		if (option.isHint) {
			return (
				<li key={key} {...optionProps} style={{ opacity: 1 }}>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", px: 1, py: 1 }}>
						<Typography variant="body1" fontSize="10px" color="text.secondary" fontWeight="bold" fontStyle="italic">
							{option.text}
						</Typography>
					</Box>
				</li>
			);
		}
		return (
			<li key={key} {...optionProps}>
				<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", px: 0.5, py: 0.5 }}>
					<Typography variant="body1" fontSize="13px" fontWeight="bold">
						{option.text}
					</Typography>
					{showSiteNumber && (
						<Typography variant="body1" fontSize="12px" color="text.secondary">
							{option.value}
						</Typography>
					)}
				</Box>
			</li>
		);
	};

	/**
	 * Renders a customer group with a sticky header; groups without a name
	 * (the hint row) render their items without a header.
	 *
	 * @param {object} params Group params from MUI Autocomplete.
	 * @returns {JSX.Element} The group list item.
	 */
	const renderGroup = (params) => (
		<li key={params.key}>
			{params.group !== "" && <GroupHeader>{params.group}</GroupHeader>}
			<GroupItems>{params.children}</GroupItems>
		</li>
	);

	return (
		<div
			className="site-select"
			onClick={handleContainerClick}
			style={{
				display: "flex",
				alignItems: "center",
				padding: "10px",
				cursor: "pointer",
				...(IS_LAYOUT2 && {
					border: "1px solid",
					borderColor: "#ccc",
					borderRadius: "20px",
					padding: "4px 8px",
				}),
			}}
		>
			<Tooltip title={isAdminRoute ? adminTooltip : ""} disableHoverListener={!isAdminRoute}>
				<Button
					variant={IS_LAYOUT2 ? "roundedGreyBgSquareContainedSmall" : "roundedGreyBgSquareContained"}
					sx={{ mr: 1 }}
					color="#494949"
				>
					<LocationOnIcon sx={IS_LAYOUT2 ? { fontSize: "14px" } : undefined} />
				</Button>
			</Tooltip>
			<div style={{ display: "flex", flexDirection: "column" }}>
				<Autocomplete
					openOnFocus
					disableClearable
					disabled={isAdminRoute}
					loading={isSearching}
					options={options}
					filterOptions={(list) => list}
					isOptionEqualToValue={(option, current) => option?.value === current?.value}
					getOptionLabel={(option) => option?.text || option?.label || ""}
					getOptionDisabled={(option) => Boolean(option?.disabled)}
					groupBy={groupingEnabled ? getGroupName : undefined}
					value={value}
					inputValue={isAdminRoute ? "" : inputValue}
					onInputChange={(event, newInputValue) => {
						if (!isAdminRoute) setInputValue(newInputValue);
					}}
					onOpen={handleOpen}
					onChange={handleChange}
					noOptionsText={isSearching ? t("GENERAL.LOADING_LOCATIONS") : t("GENERAL.NO_MATCHES_FOUND")}
					renderInput={renderInput}
					renderOption={renderOption}
					renderGroup={groupingEnabled ? renderGroup : undefined}
					slotProps={{
						popper: { className: "header-autocomplete-popper" },
						paper: { sx: { minWidth: 200 } },
					}}
					sx={{
						width: isMobile ? "100%" : FIELD_WIDTH,
						height: "15px",
						color: "#4f4f4f",
						"& .MuiOutlinedInput-root": {
							border: "none",
							height: "15px",
							"& fieldset": { border: "none" },
							"&:hover fieldset": { border: "none" },
							"&.Mui-focused fieldset": { border: "none" },
						},
						"& .MuiInput-root": {
							height: "15px",
						},
					}}
				/>
				{helperTextKey && (
					<Typography variant="body2" fontSize="11px" mt={0.55} color="text.secondary">
						{t(helperTextKey)}
					</Typography>
				)}
			</div>
		</div>
	);
};

export default SiteSelection;
